import { Smartphone } from 'lucide-react';
import { Link } from 'react-router';
import { Badge } from '@/components/ui';
import type { RegisterEvent, RegisterRecord } from '@/features/registers/api';
import {
  APP_CATEGORY_LABEL,
  complaintDetail,
  CONTACT_PREFERENCE_LABEL,
  eventMessage,
  isAppComplaint,
  nextDeadline,
} from './complaint-format';
import styles from './OperationalRecords.module.css';
import { dayLabel } from './register-format';

/** "App" badge for a complaint made in the patient app (RL-954). */
export function AppSourceBadge({ r }: { r: RegisterRecord }) {
  if (!isAppComplaint(r)) return null;
  return (
    <Badge tone="info" icon={<Smartphone size={12} aria-hidden="true" />}>
      From the app
    </Badge>
  );
}

/** "Acknowledge by 26 Oct 2026" / "Answer by …" with an Overdue badge. */
export function DeadlineCell({ r }: { r: RegisterRecord }) {
  const d = nextDeadline(r);
  if (!d) return <>-</>;
  return (
    <span className={styles.deadline}>
      <span className={d.overdue ? styles.late : undefined}>
        {d.label} {dayLabel(d.day)}
      </span>
      {d.overdue && <Badge tone="danger">Overdue</Badge>}
    </span>
  );
}

/** Complaint-only rows of the record drawer: source, deadlines, contact, replies. */
export function ComplaintDetails({ r }: { r: RegisterRecord }) {
  const c = complaintDetail(r);
  const app = isAppComplaint(r);
  const ackLate = !r.closedAt && r.status === 'new' && r.overdue;
  const replyLate = !r.closedAt && r.status !== 'new' && r.overdue;
  return (
    <section className={styles.section} aria-label="Complaint">
      <h3 className={styles.sectionTitle}>Complaint</h3>
      {app && (
        <p className={styles.note}>
          Made in the patient app. Acknowledging, replying and closing it send
          the patient “Update on your complaint”; they read the reply and the
          answer in the app.
        </p>
      )}
      <dl className={styles.kv}>
        <dt>Case number</dt>
        <dd className="mono">{r.number ?? '-'}</dd>
        <dt>Source</dt>
        <dd>{app ? <AppSourceBadge r={r} /> : 'Recorded by the clinic'}</dd>
        {c.appCategory && (
          <>
            <dt>About (in the app)</dt>
            <dd>{APP_CATEGORY_LABEL[c.appCategory] ?? c.appCategory}</dd>
          </>
        )}
        {c.contactPreference && (
          <>
            <dt>Contact preference</dt>
            <dd>
              {CONTACT_PREFERENCE_LABEL[c.contactPreference] ??
                c.contactPreference}
            </dd>
          </>
        )}
        <dt>Owner</dt>
        <dd>{r.owner?.fullName ?? 'Unassigned'}</dd>
        {c.acknowledgeBy && (
          <>
            <dt>Acknowledge by</dt>
            <dd>
              <Deadline day={c.acknowledgeBy} late={ackLate} />
            </dd>
          </>
        )}
        {c.replyBy && (
          <>
            <dt>Answer by</dt>
            <dd>
              <Deadline day={c.replyBy} late={replyLate} />
            </dd>
          </>
        )}
        {c.reply && (
          <>
            <dt>Last reply to the patient</dt>
            <dd className={styles.text}>{c.reply}</dd>
          </>
        )}
        {c.response && (
          <>
            <dt>Answer</dt>
            <dd className={styles.text}>{c.response}</dd>
          </>
        )}
        {r.patient && (
          <>
            <dt>Patient record</dt>
            <dd>
              <Link to={`/patients/${r.patient.id}`}>
                Open {r.patient.fullName}
              </Link>
            </dd>
          </>
        )}
      </dl>
    </section>
  );
}

function Deadline({ day, late }: { day: string; late: boolean }) {
  return (
    <span className={styles.deadline}>
      <span className={late ? styles.late : undefined}>{dayLabel(day)}</span>
      {late && <Badge tone="danger">Overdue</Badge>}
    </span>
  );
}

/** The reply / answer an event sent, and "Patient notified" (complaints). */
export function EventMessage({ e }: { e: RegisterEvent }) {
  const m = eventMessage(e);
  if (!m.text && !m.notified) return null;
  return (
    <>
      {m.text && <p className={styles.text}>{m.text}</p>}
      {m.notified && (
        <span className={styles.notified}>Patient notified in the app</span>
      )}
    </>
  );
}
